<script lang="ts">
  import { generateUniqueElementId } from '@athena-lib/unique-element-id';
  import { KeyboardArrowDown } from '@athena-lib/Icons';
  import clsx from 'clsx';
  import { Image } from '@athena-lib/Image';
  import { type DropdownProps } from './types';

  let {
    id: providedId,
    class: className,
    innerClass,
    value = $bindable(),
    label,
    options,
    disabled,
    placeholder,
    error,
    helpText,
    country = '',
    ...restProps
  }: DropdownProps = $props();

  const id = $derived(providedId ?? generateUniqueElementId());
  const message = $derived(error ?? helpText);
  const displayValue = $derived.by(() => {
    const option = options.find((opt) => opt.value === value);
    return option?.label ?? placeholder;
  });
</script>

<div class={clsx(className, 'flex w-full flex-col')}>
  <div
    class={clsx(
      'relative',
      'h-16',
      'rounded',
      'bg-white',
      'border',
      'has-[:focus]:border-st-blue-500 has-[:focus]:ring has-[:focus]:ring-st-blue-100',
      'inline-block',
      !disabled && 'border-st-gray-300',
      disabled && 'border-st-gray-400 bg-st-gray-100',
      !error && 'has-[:focus]:border-st-blue-200 has-[:focus]:ring has-[:focus]:ring-st-blue-100',
      error && 'border-st-red-500 ring ring-st-red-200',
    )}
  >
    <div
      class={clsx(
        'relative flex h-full items-center pb-2',
        label && country === '' ? 'p-4' : 'p-2',
        innerClass,
      )}
    >
      {#if country !== ''}
        <span class="absolute left-2 top-2.5">
          <Image
            src={'/api/asset-flags/' + (country?.toLowerCase() || 'us')}
            alt={`Flag of ${country}`}
            class="h-6 w-8 rounded"
            loading="lazy"
          />
        </span>
      {/if}
      {#if label}
        <label
          for={id}
          class={clsx(
            'absolute',
            'top-1',
            'text-xs',
            'font-normal',
            'tracking-wide',
            country !== '' && 'pl-10',
            disabled && 'text-st-gray-500',
          )}>{label}</label
        >
      {/if}
      {#if helpText}
        <span class="absolute right-2 top-2.5 text-xs text-st-gray-500">{helpText}</span>
      {/if}
      <span
        class={clsx(
          'absolute align-middle text-base ',
          country !== '' && 'top-2.5 line-clamp-1 pl-10',
          disabled && 'text-st-gray-500',
        )}
      >
        {displayValue}
      </span>
      <span
        class={clsx(disabled && 'text-st-gray-500', 'absolute right-2.5 top-5')}
        aria-hidden="true"
      >
        <KeyboardArrowDown sizeClass="w-6 h-6" />
      </span>
    </div>
    <select
      {...restProps}
      {id}
      {disabled}
      bind:value
      class="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      aria-invalid={!!error}
      aria-describedby={message ? `${id}-message` : undefined}
    >
      {#if placeholder}
        <option value="" selected={value === ''}>{placeholder}</option>
      {/if}
      {#each options as option (option.value)}
        <option selected={option.value === value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      {/each}
    </select>
  </div>
  {#if message}
    <span
      id={`${id}-message`}
      class={clsx('mt-1', 'text-sm', 'font-normal', 'text-st-gray-800', error && 'text-st-red-500')}
    >
      {message}
    </span>
  {/if}
</div>
